// Dormant DDA Actions — Conversions section, deterministic check.
// Surfaces primary, enabled conversion actions on data-driven attribution whose model has
// gone stale/expired, or whose 30d volume has slipped below Google's published DDA threshold
// (≈ 300/30d). EXPIRED actions are the worst case — bidding decisions flow through a model
// that no longer functions.

import type { EvidenceProps } from './registry';

type DdaHealth = 'expired' | 'stale' | 'slipping' | 'healthy';

interface ActionRow {
  id?: string;
  name?: string;
  category?: string;
  includeInConversionsMetric: boolean;
  last30dConversions: number;
  dataDrivenStatus?: string;
  health: DdaHealth;
}

interface DormantDdaShape {
  ddaThreshold30d: number;
  totalOnDda: number;
  expiredCount: number;
  staleCount: number;
  slippingCount: number;
  healthyCount: number;
  actions: ActionRow[];
}

const CATEGORY_LABEL: Record<string, string> = {
  PURCHASE: 'Purchase',
  SIGNUP: 'Sign-up',
  LEAD: 'Lead',
  ADD_TO_CART: 'Add to Cart',
  BEGIN_CHECKOUT: 'Begin Checkout',
  PAGE_VIEW: 'Page View',
  CONTACT: 'Contact',
  PHONE_CALL_LEAD: 'Call'
};

const HEALTH_LABEL: Record<DdaHealth, string> = {
  expired: 'Expired',
  stale: 'Stale',
  slipping: 'Slipping',
  healthy: 'Healthy'
};

function categoryLabel(c: string | undefined): string {
  if (!c) return '—';
  return CATEGORY_LABEL[c] ?? c;
}

function formatVolume(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return n.toLocaleString();
}

export const DormantDdaEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as DormantDdaShape;
  const issueCount = e.expiredCount + e.staleCount + e.slippingCount;

  return (
    <div className='evidence'>
      <p className='evidence__lead'>
        {issueCount === 0 ? (
          <>
            All {e.totalOnDda} primary action{e.totalOnDda === 1 ? '' : 's'} on data-driven
            attribution are healthy — DDA models are AVAILABLE and firing above the{' '}
            {e.ddaThreshold30d}/30d threshold.
          </>
        ) : (
          <>
            <strong>{issueCount}</strong> of {e.totalOnDda} primary action
            {e.totalOnDda === 1 ? '' : 's'} on data-driven attribution{' '}
            {issueCount === 1 ? 'is' : 'are'} unhealthy. DDA needs ≥ {e.ddaThreshold30d} conv/30d to
            stay live; below that the model goes stale and then expires, and bidding decisions flow
            through an attribution model that no longer functions.
          </>
        )}
      </p>

      <div className='dda-health-summary'>
        <HealthCount label='Expired' count={e.expiredCount} kind='expired' />
        <HealthCount label='Stale' count={e.staleCount} kind='stale' />
        <HealthCount label='Slipping' count={e.slippingCount} kind='slipping' />
        <HealthCount label='Healthy' count={e.healthyCount} kind='healthy' />
      </div>

      <table className='evidence__table evidence__table--dormant-dda'>
        <thead>
          <tr>
            <th>Conversion action</th>
            <th>Category</th>
            <th>DDA status</th>
            <th>Health</th>
            <th className='evidence__th--right'>Conv (30d)</th>
          </tr>
        </thead>
        <tbody>
          {e.actions.map(r => (
            <tr
              key={r.id ?? r.name}
              className={r.health !== 'healthy' ? 'evidence__row--flagged' : undefined}
            >
              <td>
                <div>{r.name ?? '(unnamed)'}</div>
                {r.id && <div className='evidence__action-id'>{r.id}</div>}
              </td>
              <td>{categoryLabel(r.category)}</td>
              <td>{r.dataDrivenStatus ?? '—'}</td>
              <td>
                <span className={`evidence__pill evidence__pill--health-${r.health}`}>
                  {HEALTH_LABEL[r.health]}
                </span>
              </td>
              <td className='evidence__td--right'>{formatVolume(r.last30dConversions)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className='evidence__footnote'>
        Health = derived from Google&apos;s DDA model status (AVAILABLE / STALE / EXPIRED) plus
        last-30d volume vs. the {e.ddaThreshold30d}/30d threshold. &ldquo;Slipping&rdquo; means the
        model is still AVAILABLE but volume has dropped under the threshold — next signal to expect
        is a STALE flag from Google.
      </p>
    </div>
  );
};

const HealthCount = ({ label, count, kind }: { label: string; count: number; kind: DdaHealth }) => (
  <div className={`dda-health-summary__cell dda-health-summary__cell--${kind}`}>
    <div className='dda-health-summary__num'>{count.toLocaleString()}</div>
    <div className='dda-health-summary__lbl'>{label}</div>
  </div>
);
